import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useCompanies } from "../../features/companies/hooks/useCompanies.js";
import { UNASSIGNED_COMPANY, getRecordCompanyKey } from "../utils/companyScope.js";
import { ensurePartyCompanies } from "../../features/customers/services/partyCompanyMigration.js";
import { ensureItemCompanies } from "../../features/items/services/itemCompanyMigration.js";

/**
 * The company the user is working in, picked once in the header. Invoices and purchase bills are
 * created, listed and edited only for this company. Remembered on this device; starts on the
 * default company. While a bill is being created or edited the picker is locked (see useCompanyLock).
 */
const STORAGE_KEY = "activeCompanyId";
const ActiveCompanyContext = createContext(null);

const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch (_) {
    return "";
  }
};

export function ActiveCompanyProvider({ children }) {
  const { companies, defaultCompany, loading } = useCompanies();
  const [storedId, setStoredId] = useState(readStored);
  const [lockCount, setLockCount] = useState(0);

  // A remembered company that was deleted (or none yet) falls back to the default company
  const activeCompany = useMemo(
    () => companies.find((c) => c.id === storedId) || defaultCompany || null,
    [companies, storedId, defaultCompany]
  );
  const activeCompanyId = activeCompany?.id || "";

  const setActiveCompany = useCallback((id) => {
    setStoredId(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch (_) {
      // Storage blocked (private mode) — the choice lasts for this visit only
    }
  }, []);

  /** True when a bill belongs to the working company. Bills with no company count as the default company's. */
  const ownsRecord = useCallback((record) => {
    if (!record || !activeCompanyId) return true;
    const key = getRecordCompanyKey(record, companies);
    return key === activeCompanyId || (key === UNASSIGNED_COMPANY && activeCompanyId === defaultCompany?.id);
  }, [companies, activeCompanyId, defaultCompany]);

  /** The company a bill belongs to (default company for bills with none). */
  const companyOfRecord = useCallback((record) => {
    const key = getRecordCompanyKey(record, companies);
    return key === UNASSIGNED_COMPANY ? defaultCompany : companies.find((c) => c.id === key) || null;
  }, [companies, defaultCompany]);

  // One-time: give existing parties and items their companies (from their bills) once companies are known
  useEffect(() => {
    if (!loading && companies.length) {
      ensurePartyCompanies({ companies, defaultCompany });
      ensureItemCompanies({ companies, defaultCompany });
    }
  }, [loading, companies, defaultCompany]);

  const lock = useCallback(() => setLockCount((n) => n + 1), []);
  const unlock = useCallback(() => setLockCount((n) => Math.max(0, n - 1)), []);

  const value = useMemo(() => ({
    companies,
    defaultCompany,
    loading,
    activeCompany,
    activeCompanyId,
    setActiveCompany,
    ownsRecord,
    companyOfRecord,
    isLocked: lockCount > 0,
    lock,
    unlock
  }), [companies, defaultCompany, loading, activeCompany, activeCompanyId, setActiveCompany, ownsRecord, companyOfRecord, lockCount, lock, unlock]);

  return <ActiveCompanyContext.Provider value={value}>{children}</ActiveCompanyContext.Provider>;
}

export function useActiveCompany() {
  const ctx = useContext(ActiveCompanyContext);
  if (!ctx) throw new Error("useActiveCompany must be used inside ActiveCompanyProvider");
  return ctx;
}

/** Locks the header company picker while a create / edit screen is open. */
export function useCompanyLock(isOpen) {
  const { lock, unlock } = useActiveCompany();
  useEffect(() => {
    if (!isOpen) return undefined;
    lock();
    return unlock;
  }, [isOpen, lock, unlock]);
}

export default ActiveCompanyContext;
